import React, { useMemo, useState, useEffect } from "react";
import { Card, Typography, Button, Segmented, Select, Switch, Space, Divider, Alert, Tooltip, Badge } from "antd";
import { Mafs, Line, Point, Text, Theme } from "mafs";

const { Title, Text: AntText } = Typography;

/**
 * TwoWayTableExplorer (flagship feedback visualisation)
 *
 * Flagship upgrades:
 * - Highlight modes that teach denominators: CELL / ROW / COLUMN / INTERSECTION
 * - Totals always-on in demoMode
 * - One-click presets (2x2 + 3x2)
 * - Counts vs Percent view
 * - Quick "Compute P(A|B)" callout with numerator/denominator spelled out
 * - Small Mafs "probability bar" visual so conditional probability feels physical
 */
export const TwoWayTableExplorer = ({ config }) => {
  const safeInt = (n, fallback = 0) => {
    const x = typeof n === "number" ? n : Number(n);
    if (!Number.isFinite(x)) return fallback;
    return Math.round(x);
  };

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  const PRESETS = useMemo(
    () => ({
      TWO_BY_TWO_SPORT_HOMEWORK: {
        label: "2×2 Sport & Homework",
        rowCategories: ["Plays sport", "Doesn't play sport"],
        colCategories: ["Completed homework", "Not completed"],
        values: [
          [30, 10],
          [25, 35],
        ],
        focusRowIndex: 0,
        focusColIndex: 0,
      },
      THREE_BY_TWO_WALK_SCHOOL: {
        label: "3×2 Walk to School",
        rowCategories: ["Year 7", "Year 8", "Year 9"],
        colCategories: ["Walks", "Doesn't walk"],
        values: [
          [12, 18],
          [15, 15],
          [18, 12],
        ],
        focusRowIndex: 2,
        focusColIndex: 0,
      },
    }),
    []
  );

  const normalized = useMemo(() => {
    const cfg = config || {};

    // Prefer preset if provided, otherwise fallback to an internal demo-safe preset.
    const presetKey = (cfg.preset || "").toString().trim();
    const preset = PRESETS[presetKey];

    const rowCategories = Array.isArray(cfg.rowCategories) && cfg.rowCategories.length > 0 ? cfg.rowCategories : preset?.rowCategories || PRESETS.TWO_BY_TWO_SPORT_HOMEWORK.rowCategories;
    const colCategories = Array.isArray(cfg.colCategories) && cfg.colCategories.length > 0 ? cfg.colCategories : preset?.colCategories || PRESETS.TWO_BY_TWO_SPORT_HOMEWORK.colCategories;

    // Values: accept nested arrays; coerce to integer counts.
    const rawVals = Array.isArray(cfg.values) && cfg.values.length > 0 ? cfg.values : preset?.values || PRESETS.TWO_BY_TWO_SPORT_HOMEWORK.values;
    const values = rowCategories.map((_, r) =>
      colCategories.map((__, c) => safeInt(rawVals?.[r]?.[c], 0))
    );

    const demoMode = cfg.demoMode !== false;
    const showTotals = demoMode || cfg.showTotals !== false;

    const displayMode = (cfg.displayMode || "COUNTS").toString().toUpperCase();

    const highlightMode = (cfg.highlight?.mode || "INTERSECTION").toString().toUpperCase();
    const showDenominator = cfg.highlight?.showDenominator !== false;

    const conditionalOn = (cfg.conditionalOn || "ROW").toString().toUpperCase(); // ROW or COLUMN

    // Focus: indexes are preferred; fall back to labels.
    const resolveIndexByLabel = (labels, candidate) => {
      if (!candidate) return null;
      const s = candidate.toString();
      const idx = labels.findIndex((x) => x?.toString() === s);
      return idx >= 0 ? idx : null;
    };

    const rowIdxFromLabel = resolveIndexByLabel(rowCategories, cfg.focusRowLabel);
    const colIdxFromLabel = resolveIndexByLabel(colCategories, cfg.focusColLabel);

    const focusRowIndex = clamp(
      Number.isInteger(cfg.focusRowIndex) ? cfg.focusRowIndex : (Number.isInteger(preset?.focusRowIndex) ? preset.focusRowIndex : (rowIdxFromLabel ?? 0)),
      0,
      Math.max(0, rowCategories.length - 1)
    );

    const focusColIndex = clamp(
      Number.isInteger(cfg.focusColIndex) ? cfg.focusColIndex : (Number.isInteger(preset?.focusColIndex) ? preset.focusColIndex : (colIdxFromLabel ?? 0)),
      0,
      Math.max(0, colCategories.length - 1)
    );

    const prompt = (cfg.prompt || "").toString().trim();

    return {
      rowCategories,
      colCategories,
      values,
      demoMode,
      showTotals,
      displayMode,
      highlightMode,
      showDenominator,
      conditionalOn,
      focusRowIndex,
      focusColIndex,
      prompt,
      presetKey: presetKey || "CUSTOM",
    };
  }, [config, PRESETS]);

  const [highlightMode, setHighlightMode] = useState(normalized.highlightMode);
  const [conditionalOn, setConditionalOn] = useState(normalized.conditionalOn);
  const [displayMode, setDisplayMode] = useState(normalized.displayMode);
  const [focusRowIndex, setFocusRowIndex] = useState(normalized.focusRowIndex);
  const [focusColIndex, setFocusColIndex] = useState(normalized.focusColIndex);

  useEffect(() => {
    setHighlightMode(normalized.highlightMode);
    setConditionalOn(normalized.conditionalOn);
    setDisplayMode(normalized.displayMode);
    setFocusRowIndex(normalized.focusRowIndex);
    setFocusColIndex(normalized.focusColIndex);
  }, [normalized]);

  const rowTotals = useMemo(() => normalized.values.map((r) => r.reduce((a, b) => a + b, 0)), [normalized.values]);
  const colTotals = useMemo(() => {
    const out = normalized.colCategories.map(() => 0);
    normalized.values.forEach((r) => r.forEach((v, c) => { out[c] += v; }));
    return out;
  }, [normalized.values, normalized.colCategories]);

  const grandTotal = useMemo(() => rowTotals.reduce((a, b) => a + b, 0), [rowTotals]);

  const cellValue = normalized.values?.[focusRowIndex]?.[focusColIndex] ?? 0;

  const formatPct = (p) => {
    if (!Number.isFinite(p)) return "—";
    return `${(p * 100).toFixed(1)}%`;
  };

  const formatRatio = (num, den) => {
    if (!Number.isFinite(num) || !Number.isFinite(den) || den <= 0) return "—";
    const p = num / den;
    return `${num} / ${den} = ${p.toFixed(3)} (${formatPct(p)})`;
  };

  const highlightMath = useMemo(() => {
    const rowLabel = normalized.rowCategories[focusRowIndex];
    const colLabel = normalized.colCategories[focusColIndex];

    const rowDen = rowTotals[focusRowIndex] ?? 0;
    const colDen = colTotals[focusColIndex] ?? 0;

    const out = {
      title: "",
      numerator: 0,
      denominator: 0,
      explanation: "",
      expression: "",
      p: null,
      rowLabel,
      colLabel,
    };

    const mode = highlightMode;

    if (mode === "ROW") {
      out.title = `Row total: ${rowLabel}`;
      out.numerator = rowDen;
      out.denominator = grandTotal;
      out.explanation = `Probability of being in the row "${rowLabel}" (out of the whole table).`;
      out.expression = `P(${rowLabel})`;
    } else if (mode === "COLUMN") {
      out.title = `Column total: ${colLabel}`;
      out.numerator = colDen;
      out.denominator = grandTotal;
      out.explanation = `Probability of being in the column "${colLabel}" (out of the whole table).`;
      out.expression = `P(${colLabel})`;
    } else if (mode === "CELL") {
      out.title = `Cell: ${rowLabel} ∩ ${colLabel}`;
      out.numerator = cellValue;
      out.denominator = grandTotal;
      out.explanation = `Joint probability for this cell (out of the whole table).`;
      out.expression = `P(${rowLabel} ∩ ${colLabel})`;
    } else {
      // INTERSECTION = conditional.
      if (conditionalOn === "COLUMN") {
        out.title = `Conditional: P(${rowLabel} | ${colLabel})`;
        out.numerator = cellValue;
        out.denominator = colDen;
        out.explanation = `Conditional means restrict to the column first: "out of ${colLabel}".`;
        out.expression = `P(${rowLabel} | ${colLabel})`;
      } else {
        out.title = `Conditional: P(${colLabel} | ${rowLabel})`;
        out.numerator = cellValue;
        out.denominator = rowDen;
        out.explanation = `Conditional means restrict to the row first: "out of ${rowLabel}".`;
        out.expression = `P(${colLabel} | ${rowLabel})`;
      }
    }

    out.p = out.denominator > 0 ? out.numerator / out.denominator : null;
    return out;
  }, [highlightMode, conditionalOn, normalized.rowCategories, normalized.colCategories, focusRowIndex, focusColIndex, rowTotals, colTotals, cellValue, grandTotal]);

  const tableColumns = useMemo(() => {
    const cols = [];

    cols.push({
      title: "",
      dataIndex: "__row",
      key: "__row",
      width: 220,
      render: (v) => <span className="ch-twtable-rowhdr">{v}</span>,
    });

    normalized.colCategories.forEach((cLabel, ci) => {
      cols.push({
        title: cLabel,
        dataIndex: `c_${ci}`,
        key: `c_${ci}`,
        align: "center",
        render: (v, row, ri) => {
          const num = typeof v === "number" ? v : safeInt(v, 0);
          const pct = grandTotal > 0 ? num / grandTotal : 0;
          const show = displayMode === "PERCENT" ? formatPct(pct) : num;

          const cellClass = cellClassName({ ri, ci, highlightMode, conditionalOn, focusRowIndex, focusColIndex });

          return (
            <div className={`ch-twtable-cell ${cellClass}`.trim()}>
              <span className="ch-twtable-cellval">{show}</span>
            </div>
          );
        },
      });
    });

    if (normalized.showTotals) {
      cols.push({
        title: "Total",
        dataIndex: "__rowTotal",
        key: "__rowTotal",
        align: "center",
        render: (v, row, ri) => {
          const isFocusRow = ri === focusRowIndex;
          const cls = isFocusRow && (highlightMode === "ROW" || (highlightMode === "INTERSECTION" && conditionalOn === "ROW"))
            ? "is-denominator"
            : "";
          return <div className={`ch-twtable-total ${cls}`.trim()}>{v}</div>;
        },
      });
    }

    return cols;
  }, [normalized.colCategories, normalized.showTotals, displayMode, grandTotal, focusRowIndex, focusColIndex, highlightMode, conditionalOn]);

  const dataSource = useMemo(() => {
    const rows = normalized.rowCategories.map((rLabel, ri) => {
      const row = {
        key: `r_${ri}`,
        __row: rLabel,
        __rowTotal: rowTotals[ri] ?? 0,
      };

      normalized.colCategories.forEach((_, ci) => {
        row[`c_${ci}`] = normalized.values?.[ri]?.[ci] ?? 0;
      });

      return row;
    });

    // Grand total row
    if (normalized.showTotals) {
      const totalRow = {
        key: "__total",
        __row: "Total",
        __rowTotal: grandTotal,
      };
      normalized.colCategories.forEach((_, ci) => {
        totalRow[`c_${ci}`] = colTotals[ci] ?? 0;
      });
      rows.push(totalRow);
    }

    return rows;
  }, [normalized.rowCategories, normalized.colCategories, normalized.values, normalized.showTotals, rowTotals, colTotals, grandTotal]);

  const reset = () => {
    setHighlightMode(normalized.highlightMode);
    setConditionalOn(normalized.conditionalOn);
    setDisplayMode(normalized.displayMode);
    setFocusRowIndex(normalized.focusRowIndex);
    setFocusColIndex(normalized.focusColIndex);
  };

  const applyPreset = (key) => {
    const p = PRESETS[key];
    if (!p) return;
    setHighlightMode("INTERSECTION");
    setConditionalOn("ROW");
    setDisplayMode("COUNTS");
    setFocusRowIndex(p.focusRowIndex ?? 0);
    setFocusColIndex(p.focusColIndex ?? 0);
  };

  const showConditionalControls = highlightMode === "INTERSECTION";

  const probabilityValue = highlightMath.p;

  return (
    <Card className="ch-twtable-widget" bodyStyle={{ padding: 16 }}>
      <div className="ch-twtable-header">
        <div className="ch-twtable-title">
          <Title level={4} style={{ margin: 0 }}>Two-way Table Explorer</Title>
          {normalized.demoMode && <Badge className="ch-twtable-pill" count="Demo" />}
        </div>

        <Space wrap>
          <Button onClick={reset} className="ch-btn-outline">Reset</Button>
        </Space>
      </div>

      {normalized.prompt && (
        <div className="ch-twtable-prompt">
          <AntText><b>Prompt:</b> {normalized.prompt}</AntText>
        </div>
      )}

      {/* Flagship controls */}
      <div className="ch-twtable-controls">
        <div className="ch-twtable-controls-row">
          <div className="ch-twtable-control">
            <AntText className="ch-twtable-label">Highlight</AntText>
            <Segmented
              options={[
                { label: "Cell", value: "CELL" },
                { label: "Row", value: "ROW" },
                { label: "Column", value: "COLUMN" },
                { label: "Intersection", value: "INTERSECTION" },
              ]}
              value={highlightMode}
              onChange={setHighlightMode}
            />
          </div>

          <div className="ch-twtable-control">
            <AntText className="ch-twtable-label">View</AntText>
            <Segmented
              options={[
                { label: "Counts", value: "COUNTS" },
                { label: "% of total", value: "PERCENT" },
              ]}
              value={displayMode}
              onChange={setDisplayMode}
            />
          </div>

          {showConditionalControls && (
            <div className="ch-twtable-control">
              <AntText className="ch-twtable-label">Compute</AntText>
              <Segmented
                options={[
                  { label: "P(col | row)", value: "ROW" },
                  { label: "P(row | col)", value: "COLUMN" },
                ]}
                value={conditionalOn}
                onChange={setConditionalOn}
              />
            </div>
          )}
        </div>

        <div className="ch-twtable-controls-row">
          <div className="ch-twtable-control">
            <AntText className="ch-twtable-label">Focus row</AntText>
            <Select
              className="ch-twtable-select"
              value={focusRowIndex}
              onChange={setFocusRowIndex}
              options={normalized.rowCategories.map((label, idx) => ({ value: idx, label }))}
            />
          </div>

          <div className="ch-twtable-control">
            <AntText className="ch-twtable-label">Focus column</AntText>
            <Select
              className="ch-twtable-select"
              value={focusColIndex}
              onChange={setFocusColIndex}
              options={normalized.colCategories.map((label, idx) => ({ value: idx, label }))}
            />
          </div>

          <div className="ch-twtable-control ch-twtable-presets">
            <AntText className="ch-twtable-label">Presets</AntText>
            <Space wrap>
              {Object.entries(PRESETS).map(([key, p]) => (
                <Button key={key} onClick={() => applyPreset(key)} className="ch-btn-outline">
                  {p.label}
                </Button>
              ))}
            </Space>
          </div>
        </div>

        <Alert
          className="ch-twtable-misconception"
          type="info"
          showIcon
          message="Misconception hook"
          description="Conditional probability means you restrict to a group first — the denominator changes (row total vs column total)."
        />
      </div>

      <Divider style={{ margin: "12px 0" }} />

      {/* Table */}
      <div className="ch-twtable-tableWrap">
        <Table
          className="ch-twtable-table"
          size="small"
          pagination={false}
          columns={tableColumns}
          dataSource={dataSource}
          rowClassName={(record, ri) => (record.key === "__total" ? "ch-twtable-grandrow" : "")}
        />
      </div>

      {/* Denominator / computation panel */}
      <div className="ch-twtable-callout">
        <div className="ch-twtable-calloutHeader">
          <AntText><b>{highlightMath.title}</b></AntText>
          {normalized.showDenominator && (
            <Tooltip title="Denominator shows the group you restrict to first (whole table, a row, or a column).">
              <span className="ch-twtable-denTag">Denominator shown</span>
            </Tooltip>
          )}
        </div>

        <div className="ch-twtable-calloutBody">
          <div className="ch-twtable-equation">
            <AntText className="ch-twtable-expr">{highlightMath.expression}</AntText>
            <AntText className="ch-twtable-ratio">{formatRatio(highlightMath.numerator, highlightMath.denominator)}</AntText>
          </div>
          <AntText type="secondary">{highlightMath.explanation}</AntText>
        </div>

        {/* Mafs probability bar */}
        <div className="ch-twtable-mafsWrap">
          <Mafs viewBox={{ x: [0, 1], y: [-1, 1] }} zoom={false} pan={false} preserveAspectRatio="xMidYMid meet">
            <Line.Segment point1={[0, 0]} point2={[1, 0]} color={Theme.gray} />
            {Number.isFinite(probabilityValue) && (
              <>
                <Line.Segment point1={[0, 0]} point2={[probabilityValue, 0]} color={Theme.indigo} weight={4} />
                <Point x={probabilityValue} y={0} color={Theme.indigo} />
                <Text x={probabilityValue} y={0.25} size={16} color={Theme.indigo}>
                  {formatPct(probabilityValue)}
                </Text>
              </>
            )}
          </Mafs>
        </div>
      </div>

      {/* Quick teacher-friendly totals note */}
      {normalized.showTotals && (
        <div className="ch-twtable-foot">
          <AntText type="secondary">Totals are shown to make the denominator explicit (row totals, column totals, grand total).</AntText>
        </div>
      )}
    </Card>
  );
};

function cellClassName({ ri, ci, highlightMode, conditionalOn, focusRowIndex, focusColIndex }) {
  const isFocusRow = ri === focusRowIndex;
  const isFocusCol = ci === focusColIndex;

  if (highlightMode === "ROW") {
    return isFocusRow ? "is-highlight" : "";
  }
  if (highlightMode === "COLUMN") {
    return isFocusCol ? "is-highlight" : "";
  }
  if (highlightMode === "CELL") {
    return isFocusRow && isFocusCol ? "is-highlight" : "";
  }

  // INTERSECTION (conditional):
  // - numerator = focused cell
  // - denominator = row total (P(col|row)) or col total (P(row|col))
  if (isFocusRow && isFocusCol) return "is-numerator";

  if (conditionalOn === "ROW") {
    return isFocusRow ? "is-denominator" : "";
  }
  return isFocusCol ? "is-denominator" : "";
}
